import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "next-i18next";
import Reveal from "../../../../components/LandingPage/v2/Reveal";
import {
	SectionV2,
	InnerV2,
	HeadV2,
	GlassCard,
	PillButton,
} from "../../../../components/LandingPage/v2/primitives";
import { properties as cases } from "../../../../data/successCases";
import IdeaSvg from "/public/landings/assets/icons/business-division/idea";
import SettingsSvg from "/public/landings/assets/icons/business-division/settings";
import LaptopSvg from "/public/landings/assets/icons/business-division/laptop";
import MarketSvg from "/public/landings/assets/icons/business-division/market";
import StarSvg from "/public/landings/assets/icons/business-division/star";
import CodeChatSvg from "/public/landings/assets/icons/business-division/code-chat";

const productIconsMap = {
	idea: <IdeaSvg />,
	settings: <SettingsSvg />,
	laptop: <LaptopSvg />,
	market: <MarketSvg />,
	star: <StarSvg />,
	"code-chat": <CodeChatSvg />,
};

/**
 * v2 of the old products tabs: selector rail on the left, glass detail panel
 * with the product pitch and the matching success-case numbers on the right.
 */
const Products = ({ divisionId, products, selectedLanguage, theme }) => {
	const { t } = useTranslation();
	const [selected, setSelected] = useState(0);

	const product = products?.[selected];
	const relatedCase = cases.find((c) => c?.id === product?.about);

	const title = t(`businessDivisionV2.productsTitle_${divisionId}`);
	const titleWords = title.split(" ");
	const lastWord = titleWords.pop();

	return (
		<SectionV2 $accent={theme.accent} $glowAt="50% 100%">
			<InnerV2>
				<Reveal>
					<HeadV2 $accent={theme.accent}>
						<span className="overline">
							{t("businessDivisionV2.productsOverline")}
						</span>
						<h2>
							{titleWords.join(" ")} <em>{lastWord}</em>
						</h2>
					</HeadV2>
				</Reveal>

				<Reveal y={34} delay={120}>
					<Layout>
						<Rail role="tablist" aria-orientation="vertical">
							{products?.map((p, i) => (
								<RailItem
									key={p.id + i}
									role="tab"
									type="button"
									aria-selected={i === selected}
									$accent={theme.accent}
									$active={i === selected}
									onClick={() => setSelected(i)}
								>
									<span className="icon" aria-hidden="true">
										{productIconsMap[p.icon]}
									</span>
									<span className="name">{p.name[selectedLanguage]}</span>
								</RailItem>
							))}
						</Rail>

						{product && (
							<Detail key={product.id + selected} $accent={theme.accent}>
								<div className="pitch">
									<span className="icon" aria-hidden="true">
										{productIconsMap[product.icon]}
									</span>
									<h3>{product.name[selectedLanguage]}</h3>
									<p>{product.description[selectedLanguage]}</p>
									<PillButton
										as="a"
										href={product.cta?.href}
										$accent={theme.accent}
									>
										{product.cta?.text[selectedLanguage]}
										<svg
											width="15"
											height="15"
											viewBox="0 0 16 16"
											fill="none"
											aria-hidden="true"
										>
											<path
												d="M3 8h10M9 4l4 4-4 4"
												stroke="currentColor"
												strokeWidth="1.6"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</PillButton>
								</div>

								{relatedCase && (
									<aside className="case">
										<span className="label">
											{t("businessDivisionV2.inPractice")}
										</span>
										{relatedCase.clientLogo && (
											<img
												src={relatedCase.clientLogo}
												alt={relatedCase.clientName?.[selectedLanguage] || ""}
												loading="lazy"
											/>
										)}
										<ul>
											{relatedCase.data?.map((stat, i) => (
												<li key={i}>
													<strong>{stat.number}</strong>
													<span>{stat.text[selectedLanguage]}</span>
												</li>
											))}
										</ul>
									</aside>
								)}
							</Detail>
						)}
					</Layout>
				</Reveal>
			</InnerV2>
		</SectionV2>
	);
};

const detailIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  align-items: start;

  @media (min-width: 992px) {
    grid-template-columns: 280px 1fr;
    gap: 36px;
  }
`;

const Rail = styled.nav`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 6px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 992px) {
    flex-direction: column;
    overflow: visible;
    padding-bottom: 0;
  }
`;

const RailItem = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: ${(p) =>
		p.$active ? "rgba(255, 255, 255, 0.045)" : "transparent"};
  border: 1px solid
    ${(p) => (p.$active ? p.$accent + "59" : "rgba(255, 255, 255, 0.08)")};
  border-radius: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    border-color 0.25s ease,
    background 0.25s ease;

  .icon {
    display: inline-flex;
    width: 26px;
    justify-content: center;

    svg {
      height: 24px;
      width: auto;

      path {
        fill: ${(p) => (p.$active ? p.$accent : "#5c6470")};
        transition: fill 0.25s ease;
      }
    }
  }

  .name {
    font-family: "Hind Madurai";
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.6px;
    color: ${(p) => (p.$active ? "#ffffff" : "#8b93a1")};
    text-shadow: ${(p) => (p.$active ? `0 0 20px ${p.$accent + "8c"}` : "none")};
    transition: color 0.25s ease;
  }

  &:hover {
    border-color: ${(p) => p.$accent + "66"};

    .name {
      color: #ffffff;
    }
  }
`;

const Detail = styled(GlassCard)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  padding: clamp(28px, 3.5vw, 44px);
  animation: ${detailIn} 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;

  /* the panel swaps content, not position — keep the hover lift off */
  &:hover {
    transform: none;
  }

  @media (min-width: 992px) {
    grid-template-columns: 1.2fr 1fr;
    gap: 40px;
  }

  .pitch {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .icon svg {
      height: 40px;
      width: auto;
      margin-bottom: 18px;

      path {
        fill: ${(p) => p.$accent};
      }
    }

    h3 {
      font-family: "Hind Madurai";
      font-size: clamp(1.5rem, 2.4vw, 2rem);
      font-weight: 600;
      color: #ffffff;
      margin: 0 0 12px 0;
    }

    p {
      font-family: "Hind Madurai";
      font-size: 0.98rem;
      line-height: 1.7;
      color: #a9afb8;
      margin: 0 0 26px 0;
    }

    a {
      text-decoration: none;
    }
  }

  .case {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding-top: 26px;

    @media (min-width: 992px) {
      border-top: none;
      border-left: 1px solid rgba(255, 255, 255, 0.08);
      padding-top: 0;
      padding-left: 40px;
    }

    .label {
      font-family: "Hind Madurai";
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 2.6px;
      text-transform: uppercase;
      color: ${(p) => p.$accent};
      margin-bottom: 16px;
    }

    img {
      max-height: 34px;
      max-width: 150px;
      object-fit: contain;
      margin-bottom: 22px;
    }

    ul {
      display: flex;
      flex-direction: column;
      gap: 16px;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    li {
      display: flex;
      align-items: baseline;
      gap: 12px;

      strong {
        font-family: "Hind Madurai";
        font-size: 1.35rem;
        font-weight: 700;
        color: ${(p) => p.$accent};
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }

      span {
        font-family: "Hind Madurai";
        font-size: 0.88rem;
        line-height: 1.5;
        color: #a9afb8;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default Products;
